import { getOrderShortCode } from '@mahawi/eshop-common';
import {
  EOrderStatus,
  EWarehouseOrderPairingState,
  type IWarehouseDetail,
  type IWarehouseOrderRef,
  type IWarehouseOrderReportFilters,
  type IWarehouseOrderReportMonthRow,
  type IWarehouseOrderReportOrderRow,
  WAREHOUSE_ORDER_REPORT_CURRENCY_ISO_CODE,
} from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Select,
  Space,
  Statistic,
  Table as AntdTable,
  Tag,
  Tooltip,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import DateRangeFilter from '../components/date-range-filter';
import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import TableComponent from '../components/table';
import { formatEur, formatPercent } from '../components/warehouse-order/labels';
import ProfitText from '../components/warehouse-order/profit-text';
import { EDateFormat } from '../fragments/date-format';
import {
  EDateRangePreset,
  fromInclusiveDates,
  type IDateRange,
  resolveDateRange,
  toInclusiveDates,
  toIsoDateRange,
} from '../fragments/date-range';
import { parseList } from '../fragments/parse-list';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { warehouseOrderReportLoad } from '../reducers/warehouse-order/reducer';
import { SPACING } from '../theme/tokens';
import { EWarehouseOrdersSearchParam } from './warehouse-orders-container';

enum EReportSearchParam {
  FROM = 'from',
  TO = 'to',
  WAREHOUSE = 'warehouse',
  STATUS = 'status',
}

const DEFAULT_EXCLUDED_ORDER_STATUSES: EOrderStatus[] = [
  EOrderStatus.CANCELLED,
  EOrderStatus.CREATED,
  EOrderStatus.PROCESSING_PAYMENT,
  EOrderStatus.REFUNDED,
];

const DEFAULT_ORDER_STATUSES: EOrderStatus[] = Object.values(
  EOrderStatus,
).filter(
  (status: EOrderStatus): boolean =>
    !DEFAULT_EXCLUDED_ORDER_STATUSES.includes(status),
);

const ORDER_STATUS_OPTIONS = Object.values(EOrderStatus).map(
  (status: EOrderStatus) => ({ value: status, label: status }),
);

type TMonthRow = IWarehouseOrderReportMonthRow & { key: string };
type TOrderRow = IWarehouseOrderReportOrderRow & { key: string };

const monthColumns: ColumnsType<TMonthRow> = [
  {
    title: 'Month',
    dataIndex: 'month',
    render: (month: string): string => dayjs(`${month}-01`).format('MMMM YYYY'),
  },
  { title: 'Orders', dataIndex: 'orderCount', align: 'right' },
  {
    title: 'Revenue',
    dataIndex: 'revenueEur',
    align: 'right',
    render: formatEur,
  },
  {
    title: 'Cost',
    dataIndex: 'purchasePriceEur',
    align: 'right',
    render: formatEur,
  },
  {
    title: 'Profit',
    dataIndex: 'profitEur',
    align: 'right',
    render: (profitEur: number): React.ReactElement => (
      <ProfitText value={profitEur} />
    ),
  },
  {
    title: 'Margin',
    dataIndex: 'marginPct',
    align: 'right',
    render: formatPercent,
  },
];

const orderColumns: ColumnsType<TOrderRow> = [
  {
    title: 'Order',
    dataIndex: 'orderUuid',
    render: (orderUuid: string): React.ReactElement => (
      <Link
        to={`/order/${orderUuid}`}
        label={getOrderShortCode(orderUuid)}
        openInNewTab
      />
    ),
  },
  {
    title: 'Created',
    dataIndex: 'orderCreatedAt',
    render: (orderCreatedAt: string): string =>
      dayjs(orderCreatedAt).format(EDateFormat.DATE),
    sorter: (a: TOrderRow, b: TOrderRow): number =>
      dayjs(a.orderCreatedAt).unix() - dayjs(b.orderCreatedAt).unix(),
  },
  { title: 'Status', dataIndex: 'orderStatus' },
  {
    title: 'Warehouse orders',
    render: (_value: string, record: TOrderRow): React.ReactElement => (
      <Space size={SPACING.xs} wrap>
        {record.warehouseOrders.map(
          (warehouseOrder: IWarehouseOrderRef): React.ReactElement => (
            <Link
              key={warehouseOrder.uuid}
              to={`/orders/warehouse-order/${warehouseOrder.uuid}`}
              label={`${warehouseOrder.warehouse.name} ${warehouseOrder.code}`}
              openInNewTab
            />
          ),
        )}
      </Space>
    ),
  },
  {
    title: 'Revenue',
    dataIndex: 'revenueEur',
    align: 'right',
    render: formatEur,
    sorter: (a: TOrderRow, b: TOrderRow): number =>
      (a.revenueEur ?? 0) - (b.revenueEur ?? 0),
  },
  {
    title: 'Cost',
    dataIndex: 'purchasePriceEur',
    align: 'right',
    render: formatEur,
  },
  {
    title: 'Profit',
    dataIndex: 'profitEur',
    align: 'right',
    render: (
      profitEur: number | null,
      record: TOrderRow,
    ): React.ReactElement => (
      <Space size={SPACING.xs}>
        <ProfitText value={profitEur} />
        {!record.isCostComplete && (
          <Tooltip
            title={`Only ${record.coveredQuantity} of ${record.orderedQuantity} pieces have a purchase price, the profit is overstated.`}
          >
            <Tag color="orange">Incomplete cost</Tag>
          </Tooltip>
        )}
      </Space>
    ),
    sorter: (a: TOrderRow, b: TOrderRow): number =>
      (a.profitEur ?? 0) - (b.profitEur ?? 0),
  },
  {
    title: 'Margin',
    dataIndex: 'marginPct',
    align: 'right',
    render: formatPercent,
    sorter: (a: TOrderRow, b: TOrderRow): number =>
      (a.marginPct ?? 0) - (b.marginPct ?? 0),
  },
];

function WarehouseOrderReportContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const report = useSelector((s: RootState) => s.WarehouseOrder.report);
  const inProcess = useSelector((s: RootState) => s.WarehouseOrder.inProcess);
  const warehouses = useSelector((s: RootState) => s.Warehouse.warehouses);
  const [searchParams, setSearchParams] = useSearchParams();

  const from: string | null = searchParams.get(EReportSearchParam.FROM);
  const to: string | null = searchParams.get(EReportSearchParam.TO);
  const range: IDateRange =
    from && to
      ? fromInclusiveDates(from, to)
      : resolveDateRange(EDateRangePreset.THIS_YEAR);
  const statusParam: string[] = parseList(
    searchParams.get(EReportSearchParam.STATUS),
  );
  const orderStatuses: EOrderStatus[] =
    statusParam.length > 0
      ? Object.values(EOrderStatus).filter((status: EOrderStatus) =>
          statusParam.includes(status),
        )
      : DEFAULT_ORDER_STATUSES;
  const filters: IWarehouseOrderReportFilters = {
    ...toIsoDateRange(range),
    warehouseUuids: parseList(searchParams.get(EReportSearchParam.WAREHOUSE)),
    orderStatuses,
  };
  const filtersKey: string = JSON.stringify(filters);

  const load = (): void => {
    dispatch(warehouseOrderReportLoad(filters));
  };

  useEffect(load, [dispatch, filtersKey]);

  const setParam = (key: EReportSearchParam, value: string): void => {
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }

    searchParams.delete('page');
    setSearchParams(searchParams);
  };

  const [reviewFrom, reviewTo]: [string, string] = toInclusiveDates(range);
  const reviewSearchParams = new URLSearchParams({
    [EWarehouseOrdersSearchParam.PAIRING]: [
      EWarehouseOrderPairingState.SUGGESTED,
      EWarehouseOrderPairingState.UNPAIRED,
    ].join(','),
    [EWarehouseOrdersSearchParam.FROM]: reviewFrom,
    [EWarehouseOrdersSearchParam.TO]: reviewTo,
    [EWarehouseOrdersSearchParam.PAGE]: '1',
  });

  const monthRows = useMemo<TMonthRow[]>(
    () =>
      (report?.months ?? []).map((month: IWarehouseOrderReportMonthRow) => ({
        key: month.month,
        ...month,
      })),
    [report],
  );

  const orderRows = useMemo<TOrderRow[]>(
    () =>
      (report?.orders ?? []).map((order: IWarehouseOrderReportOrderRow) => ({
        key: order.orderUuid,
        ...order,
      })),
    [report],
  );

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Warehouse orders profit"
        onReload={load}
        isReloadDisabled={inProcess}
      />

      <Space size={SPACING.lg} align="start" wrap>
        <DateRangeFilter
          value={range}
          disabled={inProcess}
          onChange={(nextRange: IDateRange): void => {
            const [nextFrom, nextTo]: [string, string] =
              toInclusiveDates(nextRange);

            searchParams.set(EReportSearchParam.FROM, nextFrom);
            searchParams.set(EReportSearchParam.TO, nextTo);
            searchParams.delete('page');
            setSearchParams(searchParams);
          }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Warehouse"
          style={{ minWidth: 200 }}
          options={(warehouses ?? [])
            .filter(
              (warehouse: IWarehouseDetail): boolean => !warehouse.isInternal,
            )
            .map((warehouse: IWarehouseDetail) => ({
              value: warehouse.uuid,
              label: warehouse.name,
            }))}
          value={filters.warehouseUuids}
          onChange={(values: string[]): void => {
            setParam(EReportSearchParam.WAREHOUSE, values.join(','));
          }}
        />
        <Select
          mode="multiple"
          placeholder="Our order status"
          style={{ minWidth: 320 }}
          maxTagCount="responsive"
          options={ORDER_STATUS_OPTIONS}
          value={orderStatuses}
          onChange={(values: EOrderStatus[]): void => {
            setParam(EReportSearchParam.STATUS, values.join(','));
          }}
        />
      </Space>

      {report && (
        <>
          {report.totals.missingExchangeRateCount > 0 && (
            <Alert
              type="warning"
              showIcon
              title={`${report.totals.missingExchangeRateCount} order(s) have no exchange rate for their date and are left out of the totals.`}
            />
          )}
          {report.totals.incompleteCostCount > 0 && (
            <Alert
              type="warning"
              showIcon
              title={`${report.totals.incompleteCostCount} order(s) have pieces without a purchase price (sold from stock or a warehouse order is not paired), their profit is overstated.`}
            />
          )}
          {(report.totals.unpairedWarehouseOrderCount > 0 ||
            report.totals.suggestedWarehouseOrderCount > 0) && (
            <Alert
              type="info"
              showIcon
              title={
                <Space size={SPACING.xs} wrap>
                  {`${report.totals.suggestedWarehouseOrderCount} warehouse order(s) need review and ${report.totals.unpairedWarehouseOrderCount} are unpaired in this period.`}
                  <Link
                    to={`/orders/warehouse-orders?${reviewSearchParams.toString()}`}
                    label="Review them"
                  />
                </Space>
              }
            />
          )}

          <Space size={SPACING.xxl} wrap align="start">
            <Statistic
              title="Revenue without VAT"
              value={report.totals.revenueEur}
              precision={2}
              suffix={WAREHOUSE_ORDER_REPORT_CURRENCY_ISO_CODE}
            />
            <Statistic
              title="Purchase cost"
              value={report.totals.purchasePriceEur}
              precision={2}
              suffix={WAREHOUSE_ORDER_REPORT_CURRENCY_ISO_CODE}
            />
            <Statistic
              title="Supplier fees"
              value={report.totals.feesEur}
              precision={2}
              suffix={WAREHOUSE_ORDER_REPORT_CURRENCY_ISO_CODE}
            />
            <Statistic
              title="Profit"
              value={report.totals.profitEur}
              precision={2}
              suffix={WAREHOUSE_ORDER_REPORT_CURRENCY_ISO_CODE}
            />
            <Statistic
              title="Margin"
              value={report.totals.marginPct ?? 0}
              precision={1}
              suffix="%"
            />
            <Statistic title="Orders" value={report.totals.orderCount} />
          </Space>

          <TableComponent
            columns={
              monthColumns as Parameters<typeof TableComponent>[0]['columns']
            }
            dataSource={monthRows}
            isLoading={inProcess}
            pagination={false}
            size="small"
            emptyText="No paired orders in this period"
            summary={(): React.ReactNode => (
              <AntdTable.Summary.Row>
                <AntdTable.Summary.Cell index={0}>
                  <strong>Total</strong>
                </AntdTable.Summary.Cell>
                <AntdTable.Summary.Cell index={1} align="right">
                  {monthRows.reduce(
                    (total: number, row: TMonthRow): number =>
                      total + row.orderCount,
                    0,
                  )}
                </AntdTable.Summary.Cell>
                <AntdTable.Summary.Cell index={2} align="right">
                  {formatEur(report.totals.revenueEur)}
                </AntdTable.Summary.Cell>
                <AntdTable.Summary.Cell index={3} align="right">
                  {formatEur(report.totals.purchasePriceEur)}
                </AntdTable.Summary.Cell>
                <AntdTable.Summary.Cell index={4} align="right">
                  <Tooltip title="After supplier fees">
                    <span>
                      <ProfitText value={report.totals.profitEur} />
                    </span>
                  </Tooltip>
                </AntdTable.Summary.Cell>
                <AntdTable.Summary.Cell index={5} align="right">
                  {formatPercent(report.totals.marginPct)}
                </AntdTable.Summary.Cell>
              </AntdTable.Summary.Row>
            )}
          />

          <TableComponent
            columns={
              orderColumns as Parameters<typeof TableComponent>[0]['columns']
            }
            dataSource={orderRows}
            isLoading={inProcess}
            size="small"
            scroll={{ x: 'max-content' }}
            defaultPageSize={50}
            emptyText="No paired orders in this period"
          />
        </>
      )}
    </Space>
  );
}

export default WarehouseOrderReportContainer;
